import React, { useState, useEffect } from 'react'
import Loader from '../../../../components/FundDetails/Modules/Shared/Loader.jsx'
import ErrorBoundary from '../../../../components/FundDetails/Modules/Performance/ErrorBoundary.jsx'
import { getDownloadCapGains } from '../InvestmentProductsDataSource.js'
import { isPresentFundEstimates } from '../helpers/InvestmentProductsHelper.js'
import {
  getAspect,
  getTabData,
  renderColumnCell,
  renderColumnHeader,
  getDataKey,
  sortFunds,
  downloadCapGains,
} from './IntervalFundsHelper.jsx'

function IntervalFunds() {
  const hashValue = window?.location?.hash
  const [funds, setFunds] = useState([])
  const [fundRoot, setFundRoot] = useState({})
  const [selectedSection, setSelectedSection] = useState('')
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [selectedSort, setSelectedSort] = useState('fundName')
  const [selectedSortType, setSelectedSortType] = useState('string')
  const [selectedPerformanceType] = useState('nav')
  const [selectedSortDirection, setSelectedSortDirection] = useState(true)

  const aspect = getAspect(
    selectedSection,
    selectedSort,
    selectedPerformanceType,
    fundRoot,
    selectedSortDirection
  )

  useEffect(() => {
    setSelectedSection(hashValue ?? '#performance')
  }, [])

  const initFundsData = async () => {
    if (!aspect?.tag) {
      return false
    }
    setIsDataLoading(true)
    const dataKey = getDataKey(selectedSection)
    const data = (await getTabData(aspect?.tag)) || {}
    if (data[dataKey]) {
      setFunds(data[dataKey])
      setFundRoot(data)
    }
    setIsDataLoading(false)
  }

  useEffect(() => {
    initFundsData()
  }, [selectedSection])

  useEffect(() => {
    setSelectedSection(hashValue)
  }, [hashValue])

  const onClickSort = (event) => {
    const sort = event.currentTarget.dataset.sort
    const sortType = event.currentTarget.dataset.sortType
    const sortDirection = !!parseInt(event.currentTarget.dataset.sortDirection)

    setSelectedSortType(sortType)

    if (sort === selectedSort) {
      setSelectedSortDirection(!selectedSortDirection)
    } else {
      setSelectedSort(sort)
      setSelectedSortDirection(sortDirection)
    }
  }
  return (
    <>
      <div className="u-sizeConstrained Content">
        {isDataLoading && <Loader showBackground={false} />}
        {/* TABLE */}
        {!isDataLoading && funds?.length > 0 && (
          <>
            {/* STATUS / DOWNLOAD */}
            <div className="u-flex u-flexAlignItemsCenter u-marginTop2gu">
              <div className="Form-label u-flexAuto">
                Displaying{' '}
                <span
                  className="u-textBold"
                  data-qa-automation={'displayingFundCount'}
                >
                  {(funds && funds.length) || 0}
                </span>{' '}
                fund(s)
              </div>

              {selectedSection === '#capital-gains' && getDownloadCapGains && (
                <div>
                  <button
                    className="Button Button--tertiary u-padding2gu u-textNoTransform"
                    onClick={(e) =>
                      downloadCapGains(e, 'intervalProductSummary')
                    }
                    data-analytics="download-cap-gains-distributions-landing"
                  >
                    Download Capital Gains Distributions (csv)
                  </button>
                  {isPresentFundEstimates(funds) && (
                    <>
                      {' '}
                      |{' '}
                      <button
                        className="Button Button--tertiary u-padding2gu u-textNoTransform"
                        onClick={(e) =>
                          downloadCapGains(e, 'intervalEstimates')
                        }
                        data-analytics="download-cap-gains-estimates-landing"
                      >
                        Download Capital Gains Estimates (csv)
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <table className="Table Table--reversed Table--sortable u-sm-block u-overflowX InvestmentProductTable">
              <thead
                role="rowgroup"
                className="Table-head is-sticky"
                style={{
                  top: 0,
                }}
              >
                <tr role="row" className="Table-row">
                  <th
                    colSpan={aspect.colSpan1}
                    className="Table-header u-bgColorBrandDark u-alignTop bordered"
                    role="columnheader"
                  ></th>
                  <th
                    colSpan={aspect.colSpan2}
                    className={`Table-header u-bgColorBrandDark u-padding2gu u-alignTop u-textNoWrap ${
                      aspect.colSpan3 && 'bordered'
                    }`}
                    role="columnheader"
                  >
                    {aspect.headerSummary1(funds)}
                  </th>
                  <th
                    colSpan={aspect.colSpan3}
                    className="Table-header u-bgColorBrandDark u-padding2gu u-alignTop u-textNoWrap"
                    role="columnheader"
                  >
                    {aspect.headerSummary2()}
                  </th>
                </tr>
                <tr role="row" className="Table-row">
                  {aspect.columns
                    .filter((column) => !column.hide)
                    .map((column) =>
                      renderColumnHeader(
                        column,
                        selectedSortDirection,
                        onClickSort
                      )
                    )}
                </tr>
              </thead>
              {funds.length < 1 ? (
                <tbody className="Table-body Table-row">
                  <tr>
                    <td className="Table-cell" colSpan={12}>
                      No matching funds found.
                    </td>
                  </tr>
                </tbody>
              ) : (
                sortFunds(
                  funds,
                  selectedSortDirection,
                  selectedSortType,
                  selectedSort
                ).map((fund, idx) => (
                  <tbody
                    className="Table-body Table-row"
                    key={`${selectedSection}-${idx}`}
                  >
                    <tr role="row">
                      {aspect.columns
                        .filter((column) => !column.hide)
                        .map((column) =>
                          renderColumnCell(
                            {
                              ...column,
                              key: `${selectedSection}-${idx}-${column.name}`,
                            },
                            fund
                          )
                        )}
                    </tr>
                  </tbody>
                ))
              )}
            </table>
          </>
        )}
      </div>
      <div id="ampf-trace-loaded-page"></div>
    </>
  )
}

export function IntervalFundsContainer() {
  return (
    <ErrorBoundary
      title="Interval Funds"
      message="Interval Fund information is currently unavailable due to a technical issue. Please try again later."
      isConstrained={true}
    >
      <IntervalFunds />
    </ErrorBoundary>
  )
}
